import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Target, 
  Wallet, 
  Building2, 
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Plus,
  LogOut
} from 'lucide-react';

/**
 * DesignPreviewPage V3 - RÉUNION PREMIUM - Version avec IMPACT
 * 
 * RÈGLES:
 * ✅ Blanc = base principale
 * ✅ Couleurs = accents avec CARACTÈRE
 * ✅ Calendrier = BLOC VISUEL FORT
 * ✅ Tortue = PRÉSENTE et RESSENTIE
 * ✅ Plus de dynamisme et contraste
 */

const DesignPreviewPage = () => {
  const [selectedDay, setSelectedDay] = useState(8);
  const [activeTab, setActiveTab] = useState('calendar');

  const calendarDays = Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    balance: 2500 + Math.floor(Math.random() * 500) - 250,
    hasOperations: Math.random() > 0.5
  }));

  const weekDays = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* ============================================
          FOND PRINCIPAL - Blanc avec texture subtile
          ============================================ */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 40%, #f1f5f9 100%)'
        }}
      />

      {/* ============================================
          TORTUE - PRÉSENTE ET RESSENTIE (10% opacité - VALIDÉ)
          ============================================ */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/turtle-bg.jpg)',
          backgroundSize: '140%',
          backgroundPosition: 'center 20%',
          opacity: 0.10,
          filter: 'saturate(0.7) contrast(1.1)'
        }}
      />

      {/* Accent océan en haut - plus prononcé */}
      <div 
        className="fixed top-0 left-0 right-0 h-80 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(14, 116, 144, 0.08) 0%, rgba(14, 116, 144, 0.03) 50%, transparent 100%)'
        }}
      />

      {/* Contenu principal */}
      <div className="relative z-10 max-w-[430px] mx-auto min-h-screen px-5 py-6 pb-28">
        
        {/* ============================================
            HEADER - Plus de présence
            ============================================ */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)',
                boxShadow: '0 8px 24px rgba(14, 116, 144, 0.35)'
              }}
            >
              DU
            </div>
            <div>
              <h1 className="text-slate-800 font-bold text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Content de te revoir, Démo 👋
              </h1>
              <p className="text-slate-500 text-sm">Prêt à gérer ton budget ?</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-500 hover:text-cyan-700 transition-all"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)'
              }}
            >
              <Plus className="w-5 h-5" />
            </button>
            <button 
              className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-500 hover:text-rose-500 transition-all"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)'
              }}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {/* ============================================
            SÉLECTEUR DE CALENDRIER - Plus structuré
            ============================================ */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 p-4 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
            border: '1px solid rgba(14, 116, 144, 0.1)'
          }}
        >
          <p className="text-cyan-700/60 text-xs uppercase tracking-wider mb-1 font-semibold" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Calendrier actif
          </p>
          <div className="flex items-center justify-between">
            <span className="text-slate-800 font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mon Budget Démo
            </span>
            <ChevronRight className="w-5 h-5 text-cyan-600" />
          </div>
        </motion.div>

        {/* ============================================
            ONGLETS - Plus de contraste
            ============================================ */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex gap-1.5 p-2 rounded-2xl mb-6"
          style={{
            background: 'linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%)'
          }}
        >
          {[
            { id: 'calendar', icon: Calendar, label: 'Calendrier' },
            { id: 'base', icon: Building2, label: 'Base' },
            { id: 'goals', icon: Target, label: 'Objectifs' },
            { id: 'wallets', icon: Wallet, label: 'Portefeuilles' },
            { id: 'tips', icon: Lightbulb, label: 'Astuces' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl transition-all duration-200 ${
                activeTab === tab.id 
                  ? 'shadow-md' 
                  : 'hover:bg-white/50'
              }`}
              style={activeTab === tab.id ? {
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              } : {}}
            >
              <tab.icon className={`w-5 h-5 ${
                activeTab === tab.id ? 'text-cyan-600' : 'text-slate-400'
              }`} />
              <span className={`text-[9px] font-semibold ${
                activeTab === tab.id ? 'text-slate-700' : 'text-slate-400'
              }`}>
                {tab.label}
              </span>
            </button>
          ))}
        </motion.div>

        {/* ============================================
            CARTES DE RÉSUMÉ - Plus de caractère
            ============================================ */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-3 mb-6"
        >
          {/* Entrées - Forêt */}
          <div 
            className="p-4 rounded-2xl"
            style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
              border: '1px solid rgba(16, 185, 129, 0.15)'
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-xs text-emerald-600/70 uppercase tracking-wider mb-0.5 font-semibold" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Entrées
            </p>
            <p className="text-xl font-extrabold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              +2800€
            </p>
          </div>

          {/* Sorties - Volcan */}
          <div 
            className="p-4 rounded-2xl"
            style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #fff1f2 100%)',
              boxShadow: '0 4px 16px rgba(244, 63, 94, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
              border: '1px solid rgba(244, 63, 94, 0.15)'
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' }}
              >
                <TrendingDown className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-xs text-rose-500/70 uppercase tracking-wider mb-0.5 font-semibold" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Sorties
            </p>
            <p className="text-xl font-extrabold text-rose-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              -978€
            </p>
          </div>

          {/* Solde - Océan profond */}
          <div 
            className="p-4 rounded-2xl"
            style={{
              background: 'linear-gradient(145deg, #0e7490 0%, #0369a1 100%)',
              boxShadow: '0 8px 24px rgba(14, 116, 144, 0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-xs text-cyan-100/80 uppercase tracking-wider mb-0.5 font-semibold" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Solde
            </p>
            <p className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              +2500€
            </p>
          </div>
        </motion.div>

        {/* Message motivationnel */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="text-center text-slate-600 text-sm mb-6 font-medium"
          style={{ fontFamily: 'Figtree, sans-serif' }}
        >
          Excellent ! Tu gères comme un pro 🎯
        </motion.p>

        {/* ============================================
            CALENDRIER - BLOC VISUEL FORT ET STRUCTURÉ
            ============================================ */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-3xl overflow-hidden"
          style={{
            boxShadow: '0 12px 40px rgba(14, 116, 144, 0.15), 0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid rgba(14, 116, 144, 0.1)'
          }}
        >
          {/* Header du calendrier - OCÉAN PROFOND */}
          <div 
            className="p-5 flex items-center justify-between relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 50%, #1e3a5f 100%)'
            }}
          >
            {/* Vagues subtiles en overlay */}
            <div 
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' d='M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,181.3C960,203,1056,213,1152,197.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundSize: 'cover',
                backgroundPosition: 'bottom'
              }}
            />
            
            <button 
              className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/25 transition-all active:scale-95 z-10"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-white font-bold text-xl z-10" style={{ fontFamily: 'Manrope, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
              Avril 2026
            </h2>
            <button 
              className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/25 transition-all active:scale-95 z-10"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Grille du calendrier - FOND CLAIR STRUCTURÉ */}
          <div 
            className="p-4"
            style={{
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
            }}
          >
            {/* Jours de la semaine */}
            <div className="grid grid-cols-7 gap-2 mb-3">
              {weekDays.map((day, i) => (
                <div 
                  key={day + i} 
                  className={`text-center text-xs font-bold py-2 ${
                    i === 0 || i === 6 ? 'text-rose-400' : 'text-cyan-700/60'
                  }`}
                  style={{ fontFamily: 'Figtree, sans-serif' }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Jours du mois */}
            <div className="grid grid-cols-7 gap-2">
              {/* Jours vides */}
              {[...Array(2)].map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              
              {/* Jours */}
              {calendarDays.map(({ day, balance, hasOperations }) => (
                <motion.button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  whileTap={{ scale: 0.92 }}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all duration-200`}
                  style={
                    selectedDay === day
                      ? {
                          background: 'linear-gradient(135deg, #f97316 0%, #f43f5e 100%)',
                          boxShadow: '0 8px 24px rgba(249, 115, 22, 0.4)',
                          color: 'white'
                        }
                      : day === 8
                        ? {
                            background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                            border: '2px solid #0e7490',
                            boxShadow: '0 4px 12px rgba(14, 116, 144, 0.2)'
                          }
                        : {
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
                            border: '1px solid rgba(148, 163, 184, 0.2)'
                          }
                  }
                >
                  <span 
                    className={`text-sm font-bold ${
                      selectedDay === day 
                        ? 'text-white' 
                        : day === 8 
                          ? 'text-cyan-700' 
                          : 'text-slate-700'
                    }`}
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {day}
                  </span>
                  <span 
                    className={`text-[8px] mt-0.5 font-semibold ${
                      selectedDay === day 
                        ? 'text-white/90' 
                        : balance >= 0 
                          ? 'text-emerald-500' 
                          : 'text-rose-500'
                    }`}
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    {balance >= 0 ? '+' : ''}{balance}
                  </span>
                  
                  {/* Indicateur */}
                  {hasOperations && selectedDay !== day && (
                    <div 
                      className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                      style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)' }}
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ============================================
            FAB - VOLCAN ÉNERGIQUE
            ============================================ */}
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: 'spring' }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-28 right-6 w-15 h-15 rounded-full text-white flex items-center justify-center z-50"
          style={{
            width: '60px',
            height: '60px',
            background: 'linear-gradient(135deg, #f97316 0%, #f43f5e 100%)',
            boxShadow: '0 8px 30px rgba(249, 115, 22, 0.5), 0 4px 12px rgba(244, 63, 94, 0.3)'
          }}
        >
          <Plus className="w-7 h-7" />
        </motion.button>

      </div>

      {/* ============================================
          NAVIGATION - ÉLÉGANTE ET STRUCTURÉE
          ============================================ */}
      <motion.nav 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.4, type: 'spring' }}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-[400px] h-18 rounded-full flex items-center justify-around z-50"
        style={{
          height: '72px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)',
          border: '1px solid rgba(14, 116, 144, 0.1)'
        }}
      >
        {[
          { icon: Calendar, label: 'Calendrier', active: true },
          { icon: Building2, label: 'Base' },
          { icon: Target, label: 'Objectifs' },
          { icon: Wallet, label: 'Portefeuilles' },
          { icon: Lightbulb, label: 'Astuces' },
        ].map((item, i) => (
          <button
            key={i}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all relative`}
          >
            <item.icon 
              className="w-5 h-5" 
              style={{ color: item.active ? '#0e7490' : '#94a3b8' }}
            />
            <span 
              className="text-[9px] font-semibold"
              style={{ color: item.active ? '#0e7490' : '#94a3b8' }}
            >
              {item.label}
            </span>
            {item.active && (
              <motion.div 
                layoutId="nav-indicator"
                className="absolute -bottom-1 w-6 h-1 rounded-full"
                style={{ background: 'linear-gradient(90deg, #0e7490 0%, #0369a1 100%)' }}
              />
            )}
          </button>
        ))}
      </motion.nav>

      {/* ============================================
          LÉGENDE
          ============================================ */}
      <div 
        className="fixed top-4 right-4 p-3 rounded-xl text-xs z-50 max-w-[180px]"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          border: '1px solid rgba(14, 116, 144, 0.15)'
        }}
      >
        <p className="font-bold text-cyan-700 mb-2">🎨 Preview V3</p>
        <div className="space-y-1 text-slate-600">
          <p>⬜ Blanc = base</p>
          <p>🌊 Océan profond</p>
          <p>🌴 Forêt = +</p>
          <p>🌋 Volcan = CTA</p>
          <p>🐢 Tortue 10% ✓</p>
        </div>
      </div>
    </div>
  );
};

export default DesignPreviewPage;
